import {Injectable, inject, signal} from '@angular/core'
import {PreferencesStorageIds} from '@common/global-constants'
import {JsonUtil} from '@common/utils/json.utils'
import {discountPercentageFromPrices} from '@common/utils/math.utils'
import {Action} from '@models/action'
import {ClassifiersInfo} from '@models/classifier'
import {ConfigAgent, OrderModel, RoundDecimalsConf, WarehouseProfile} from '@models/configAgent'
import type {CustomLiteralEntry} from '@models/custom-literals.models'
import {DELIVERY_DATE_DEFAULT} from '@models/constants/delivery-date.constants'
import {Endpoints} from '@models/endpoints'
import {FeatureFlags} from '@models/feature-flags.models'
import {LoadCatalogMethods} from '@models/order/loadCatalogMethods'
import {OrderType} from '@models/order/order'
import {ORDER_TYPE_LINES} from '@models/order/orderproduct'
import {Outlet} from '@models/outlet'
import {ProductImageMapper} from '@models/product'
import {Permision as UserPermission} from '@models/user-permissions'
import {UveTranslateStringPipe} from '@pipes/translate-string.pipe'
import {ApiService} from '@service/api.service'
import {StorageApiService} from '@service/storage/storage-api.service'
import {UVEApiService} from '../uve/api.service'
import type {AdditionalCost, TaxEntity, TaxProfile} from '@models/taxes'

const firstTruthy = <T>(...values: T[]): T | undefined => values.find(Boolean)

interface TaskCloud {
  id: number
  actionId: number
  title: string
  description: string
  type: {
    id: number
    name: string
  }
  mandatory: boolean
  image: string | null
  group: string | null
  enableComments: boolean
  attachedDocumentId: number | null
  executionType: number
  url: string | null
  openInNewTab: boolean | null
  orderModel: string
}

/**
 * Almacén y adaptador de estado para la ejecución embebida de Street dentro de UVE Cloud.
 *
 * @description
 * Cuando Street corre como iframe dentro de otra aplicación UVE, este servicio recibe un
 * "payload" con toda la información contextual (outlet, pedidos existentes, clasificadores,
 * parámetros de configuración, feature flags, permisos, UMs, impuestos, etc.) y lo traduce
 * a los mismos signals que la app usaría en modo standalone. Esto permite que el resto de
 * servicios y componentes operen sin saber si están en modo embebido o no.
 *
 * Además descarga datos adicionales cuando no vienen en el payload (detalles del outlet,
 * del pedido, distribuidores con modelo `PEDIDO_TRANSFER`, imágenes de productos, etc.) y
 * normaliza la información para que encaje con los modelos de Street.
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * ```typescript
 * const store = inject(EmbeddedStoreService)
 *
 * // En el bootstrap embebido, tras recibir el payload del host
 * await store.initDataOrder({id, actionId, orderId}, payload)
 * // o, para pantallas de detalle
 * await store.initDataOrdersDetail({orderId}, payload)
 * ```
 *
 * ### Best Practices
 * - Acceder a los signals (`outlet`, `configAgent`, `orders`, ...) sólo después de que
 *   `initDataOrder`/`initDataOrdersDetail` haya resuelto.
 * - No mutar los signals desde fuera: este servicio es la única fuente de verdad del
 *   payload embebido.
 *
 * ### Bad Uses / Errores comunes
 * - Llamar `initDataOrder` múltiples veces con payloads distintos durante una misma sesión:
 *   el estado se reescribe y puede dejar flags de feature en estado inconsistente.
 */
@Injectable({
  providedIn: 'root',
})
export class EmbeddedStoreService {
  private readonly uveApiService: UVEApiService = inject(UVEApiService)
  private readonly apiService: ApiService = inject(ApiService)
  private readonly storageApiService = inject(StorageApiService)
  private readonly translateStringPipe = inject(UveTranslateStringPipe)

  readonly embeddedIdComercial = 'EMBEDDED'
  customLiterals = signal<CustomLiteralEntry[]>(null)
  infoClassifiers = signal<ClassifiersInfo>({customers: [], orders: [], orderLine: [], products: []})
  allOrderLines = signal<any>(null)
  dataUms = signal<any>(null)
  dataTaxes = signal<{taxes: TaxEntity[]; taxesProfiles: TaxProfile[]; additionalCosts: AdditionalCost[]}>(null)
  configAgent = signal<ConfigAgent>(null)
  outlet = signal<Outlet>(null)
  actions = signal<any[]>([])
  orders = signal<any[]>([])
  order = signal<any>(null)
  distributors = signal<any[]>([])
  outletRelationCodes = signal<any>({})
  productsImageByIdcProduct = new Map<number, ProductImageMapper>()
  featureFlags = new Map<FeatureFlags, boolean>()
  userPermissions = signal<UserPermission[]>([])
  warehousesProfiles = signal<WarehouseProfile[]>([])

  /**
   * Inicializa el estado embebido para el flujo de creación/edición de pedido.
   *
   * @description
   * Establece plataforma, permisos, feature flags, `ConfigAgent`, clasificadores,
   * UMs, impuestos, outlet, acciones y pedidos a partir del payload. Si falta información
   * (outlet o pedidos), la descarga desde la UVE API. También precarga imágenes de
   * productos para el pedido activo y, si el modelo es `PEDIDO_TRANSFER`, resuelve los
   * códigos de relación outlet↔distribuidor.
   *
   * @param params Parámetros de ruta: `{id, actionId, orderId}`.
   * @param payload Payload enviado por la aplicación anfitriona con el contexto completo.
   */
  initDataOrder = async (params: any, payload: any): Promise<void> => {
    const {id, actionId, orderId} = params
    this.uveApiService.setPlatform(payload.platform ?? 'sfa')
    this.deleteDraftOrder(id, actionId)
    this.setPermissions(payload.permissions)
    this.setFeatureFlags(payload)
    this.loadConfigAgent(payload.params ?? [])
    this.loadClassifiers(
      this.mapClassifiers(payload.classifiers?.orders),
      this.mapClassifiers(payload.classifiers?.orderLines),
      this.mapClassifiers(payload.classifiers?.products)
    )
    this.buildAction(payload.task, actionId)
    this.outlet.set({
      ...payload.outlet,
      code: payload.outlet?.code ?? '',
      trade_name: payload.outlet?.name ?? '',
      address_text: payload.outlet?.address ?? '',
      address: {
        street_address: firstTruthy(payload.outlet?.address) ?? '',
        provinceId: firstTruthy(payload.outlet?.addressDetails?.provinceId) ?? '',
        postal_code: firstTruthy(payload.outlet?.postalCode, payload.outlet?.addressDetails?.postalCode) ?? '',
        city: firstTruthy(payload.outlet?.municipality, payload.outlet?.addressDetails?.town) ?? '',
        municipality: firstTruthy(payload.outlet?.municipality, payload.outlet?.addressDetails?.municipality) ?? '',
        country: firstTruthy(payload.outlet?.country, payload.outlet?.addressDetails?.country) ?? '',
        latitude: firstTruthy(payload.outlet?.coordX, payload.outlet?.addressDetails?.coordinateX) ?? '',
        longitude: firstTruthy(payload.outlet?.coordY, payload.outlet?.addressDetails?.coordinateY) ?? '',
      },
      taxGroup: payload.outlet?.taxGroup ?? '',
      classifiers:
        Object.keys(payload.outlet?.classifiers).reduce((r, c) => {
          r[c] = payload.outlet?.classifiers[c].value
          return r
        }, {}) ?? [],
    })

    this.orders.set(
      payload.orders?.map(o => ({
        ...o,
        products: o?.products?.map(p => ({
          ...p,
          agreementNum: undefined,
          orderedUMDescription: p?.orderedUMDesc,
          deliveredUMDescription: p?.deliveredUMDesc,
          discountPercentage: this.discountPercentageFor(p),
          discountAmount: p?.discountAmount,
          discount: p?.discount,
        })),
      })) ?? []
    )
    this.setOrderLines(payload.ordersLineTypes)
    this.setDataTaxes(payload.params)
    this.setUMS(
      payload.ums?.map(um => ({
        ...um,
        additionalInfo: {
          ...um.additionalInfo,
          esParaUsoManualGestiona: um.additionalInfo?.IsEnabled,
        },
      })),
      [
        {
          id: 'quantityCurrency',
          name: payload.currency?.description,
          type: 'SUM',
          agentId: payload.currency?.symbol,
          shortName: null,
        },
        {
          id: 'xxx',
          name: '',
          type: 'SUM',
          agentId: payload.currency?.symbol,
          shortName: null,
        },
      ]
    )
    if (orderId) {
      const order = payload?.orders.find(o => o.id === orderId || o.orderId === orderId)
      if (order) {
        this.order.set(order)
        await this.getImagesProducts(order?.products.map(p => p.productCodeByManufacturer))
      }
    }
    await Promise.all([
      id === '-1' || !!this.outlet() ? Promise.resolve() : this.getOutlet(id),
      payload?.orders === null ? this.getOrders(id, null) : Promise.resolve(),
      orderId && !this.order() ? this.getOrderById(orderId, null) : Promise.resolve(),
    ])

    if (this.order()) {
      const orderAlreadyListed = this.orders().some(x => x.id === this.order().id || x.orderId === this.order().id)
      if (!orderAlreadyListed) {
        this.orders.set([this.order(), ...this.orders()])
      }
      await Promise.all([
        this.getImagesProducts(this.order()?.products.map(p => p.productCodeByManufacturer)),
        this.getRelationOutletData(this.outlet(), this.actions(), this.order()),
      ])
    } else {
      await this.getRelationOutletData(this.outlet(), this.actions(), this.order())
    }
    this.downloadMultimediaOffers()
  }

  /**
   * Inicializa el estado embebido para el flujo de detalle de pedido (solo lectura).
   *
   * @description
   * Variante simplificada de `initDataOrder` pensada para la pantalla de detalle: no
   * descarga distribuidores ni códigos de relación, se centra en cargar el pedido
   * objetivo, marcar líneas de oferta como `readOnly` cuando corresponde, y precargar
   * imágenes de los productos.
   *
   * @param params Parámetros de ruta: `{orderId}`.
   * @param payload Payload enviado por la aplicación anfitriona.
   */
  initDataOrdersDetail = async (params: any, payload: any): Promise<void> => {
    this.uveApiService.setPlatform(payload.platform ?? 'sfa')
    this.setPermissions(payload.permissions)
    this.setFeatureFlags(payload)
    this.loadConfigAgent(payload.params ?? [])
    this.loadClassifiers(
      this.mapClassifiers(payload.classifiers?.orders),
      this.mapClassifiers(payload.classifiers?.orderLines),
      this.mapClassifiers(payload.classifiers?.products)
    )
    this.outlet.set(payload.outlet)

    this.setOrderLines(payload.ordersLineTypes)
    this.setUMS(
      payload.ums?.map(um => ({
        ...um,
        additionalInfo: {...um.additionalInfo, esParaUsoManualGestiona: um.additionalInfo?.IsEnabled},
      })),
      [
        {
          id: 'quantityCurrency',
          name: payload.currency?.description,
          type: 'SUM',
          agentId: payload.currency?.symbol,
          shortName: null,
        },
      ]
    )
    this.setDataTaxes(payload.params)
    const {orderId} = params

    await this.getOrderById(orderId, payload.order)
    const orders = payload.orders
      ?.map(o => ({
        ...o,
        products: o?.products?.map(p => ({
          ...p,
          agreementNum: undefined,
          discountPercentage: this.discountPercentageFor(p),
        })),
      }))
      .filter(o => o.id !== orderId)
    this.orders.set([this.order(), ...orders])
    await Promise.all([this.getImagesProducts(this.order()?.products.map(p => p.productCodeByManufacturer))])
  }

  private readonly discountPercentageFor = (p: {basePrice?: number; price?: number}): number =>
    discountPercentageFromPrices(p?.basePrice, p?.price)

  private readonly mapClassifiers = (classifiers: any[]): any[] =>
    classifiers?.map(gc => ({
      ...gc,
      classifiers: gc.classifiers?.map(c => ({
        ...c,
        values: c.values?.map(v => ({
          ...v,
          id: v.value ?? v.id,
          label: this.translateStringPipe.transform(v.label),
        })),
        modifiable: true,
      })),
    })) ?? []

  private readonly loadClassifiers = (orders: any[], orderLine: any[], products: any[]): void => {
    this.infoClassifiers.set({
      orders,
      orderLine,
      customers: [],
      products,
    })
  }

  private readonly setOrderLines = (orderLines: any[]): void => {
    this.allOrderLines.set({orderLines})
  }

  private readonly setCustomLiterals = (rawJson: string): void => {
    try {
      const parsed: unknown = JSON.parse(rawJson)
      if (Array.isArray(parsed)) this.customLiterals.set(parsed as CustomLiteralEntry[])
    } catch {
      /* empty */
    }
  }

  private readonly setUMS = async (allUms: any[], ums): Promise<void> => {
    this.dataUms.set({
      umsOrder: allUms.filter(um => um?.additionalInfo?.esParaUsoManualGestiona),
      ums,
      allUms,
    })
  }

  private readonly loadConfigAgent = (parameters: any[]): void => {
    const customLiteralsParam = parameters.find(p => p.parameter === 'CustomLiterals')?.value
    if (customLiteralsParam) {
      this.setCustomLiterals(customLiteralsParam)
    }

    const models = parameters.find(p => p.parameter === 'OrdersConfigurationModels')?.value
    const extensionConfigurations = parameters.find(p => p.parameter === 'ExtensionsConfiguration')?.value
    const extensionsConfigurationValue = []
    if (extensionConfigurations) {
      JSON.parse(extensionConfigurations).forEach(extension => {
        if (!extension.script && !!extension.url) {
          extension.script = `{ "Url": "${extension.url}" , "Method" : "POST", "QueryString" : "string"}`
        }
        extensionsConfigurationValue.push(extension)
      })
    }

    const warehousesProfilesParam = parameters.find(p => p.parameter === 'WarehouseProfile')?.value
    const warehousesProfiles = []
    if (warehousesProfilesParam) {
      JSON.parse(warehousesProfilesParam).forEach(wp => {
        warehousesProfiles.push({
          id: wp.id,
          warehouses: wp.warehouses,
          name: wp.name,
        })
      })
      this.warehousesProfiles.set(warehousesProfiles)
    }

    const smartOfferCollisionContract = parameters.find(p => p.parameter === 'SmartOfferCollisionContract')?.value

    const channelsConfigurationModels = parameters.find(p => p.parameter === 'ChannelsConfigurationModels')?.value

    const roundDecimalsConfig = parameters.find(p => p.parameter === 'PricesCalculationConfiguration')?.value
    const roundDecimalsConfigValue: RoundDecimalsConf = roundDecimalsConfig ? JSON.parse(roundDecimalsConfig) : null

    const deliveryDateMorningHourLimit =
      parameters.find(p => p.parameter === 'FECHA_ENTREGA_PEDIDO_HORA_LIMITE_MAÑANA')?.value?.toString() ?? ''
    const deliveryDateHourLimit =
      parameters.find(p => p.parameter === 'FECHA_ENTREGA_PEDIDO_HORA_LIMITE')?.value?.toString() ?? ''
    const deliveryDateDelayMinDays =
      parameters.find(p => p.parameter === 'FECHA_ENTREGA_PEDIDO_DIAS_MIN')?.value?.toString() ?? ''

    const ordersAdditionalStates = parameters.find(p => p.parameter === 'OrdersAdditionalStates')?.value
    this.configAgent.set({
      ordersAdditionalStates: (ordersAdditionalStates ? JSON.parse(ordersAdditionalStates) : []) ?? [],
      deliveryDateMode:
        firstTruthy(parameters.find(p => p.parameter === 'FECHA_ENTREGA_MODO')?.value) ?? 'MODE_BASIC_DELIVERY',
      deliveryDateMorningHourLimit: deliveryDateMorningHourLimit === '' ? null : Number(deliveryDateMorningHourLimit),
      deliveryDateHourLimit: deliveryDateHourLimit === '' ? 18 : Number(deliveryDateHourLimit),
      deliveryDateDelayMinDays: deliveryDateDelayMinDays === '' ? 1 : Number(deliveryDateDelayMinDays),
      deliveryDateServiceDaysCoded:
        firstTruthy(parameters.find(p => p.parameter === 'FECHA_ENTREGA_DIAS_SERVICIO')?.value) ??
        DELIVERY_DATE_DEFAULT.WORKING_DAYS,
      deliveryDateCustomerDefaultDays:
        parameters.find(p => p.parameter === 'FECHA_ENTREGA_DIAS_REPARTO_CLIENTE_DEFAULT')?.value ?? 'Lun a Vie',
      ordersValidationWebhookUrl: parameters.find(p => p.parameter === 'OrdersValidationWebhookUrl')?.value,
      loadCatalogMethod:
        parameters.find(p => p.parameter === 'CATALOGO_PEDIDOS_CARGA_METODO')?.value ?? LoadCatalogMethods.BASIC,
      quantitiesInIntermediateCalculations: roundDecimalsConfigValue?.quantitiesInIntermediateCalculations ?? null,
      roundIntermediateCalculations: roundDecimalsConfigValue?.roundIntermediateCalculations ?? null,
      roundLineTax: roundDecimalsConfigValue?.roundLineTax ?? null,
      roundPriceMode: roundDecimalsConfigValue?.roundPriceMode ?? null,
      roundUnitPrice: roundDecimalsConfigValue?.roundUnitPrice ?? null,
      roundUnitPriceCatalog: roundDecimalsConfigValue?.roundUnitPriceCatalog ?? null,
      taxesCalculationMode: roundDecimalsConfigValue?.taxesCalculationMode ?? 'Normal',
      extensionsConfiguration: extensionsConfigurationValue,
      smartOfferCollisionContract:
        smartOfferCollisionContract ? JSON.parse(smartOfferCollisionContract) : ['PrioritizeRecentOnCollision'],
      showCatalogOnOrderStreet: 'S',
      lastOrderProductsMode:
        firstTruthy(parameters.find(p => p.parameter === 'MODO_ULTIMOS_PEDIDOS_PRODUCTO')?.value) ?? null,
      lastOrdersReference:
        firstTruthy(parameters.find(p => p.parameter === 'STREET_LAST_ORDERS_REFERENCE')?.value) ?? null,
      ChannelsConfigurationModels: channelsConfigurationModels ? JSON.parse(channelsConfigurationModels) : null,
      defaultDeliverySlotsSchedules:
        firstTruthy(parameters.find(p => p.parameter === 'DEFAULT_DELIVERY_SLOTS_SCHEDULES')?.value) ?? null,
      reactiveFieldsConfiguration: parameters.find(p => p.parameter === 'ReactiveFieldsConfiguration')?.value ?? null,
      ordersConfigurationModels: (models ? JSON.parse(models) : []).map(
        model =>
          ({
            Id: model.id,
            Name: model.name,
            Type: model.type,
            Valued: model.valued,
            RequiredWebhook: model.requiredWebhook,
            OfflineMode: false,
            SendEmail: model.sendEmail,
            Visibility: model.visibility,
            UserApplicability: model.userApplicability,
            HeaderClassifiers: model.headerClassifiers?.map(c => ({
              Id: c.id,
              Value: c.value,
              Hidden: c.hidden,
            })),
            AvailableLineTypes: model.availableLineTypes,
            LineClassifiers: model.lineClassifiers?.map(c => ({Id: c.id, Hidden: c.hidden})),
            IsDefaultModel: model.isDefaultModel,
            OrderModelWeightRestriction: {
              Enabled: model.weightValidationEnabled,
            },
            VolumeRestriction: {
              Enabled: model.volumeValidationEnabled,
            },
            StatisticalMeasurementUnits: {
              ShouldCalculateStatisticalUM1: model.statisticalMeasurementUnits?.shouldCalculateStatisticalUM1,
              ShouldCalculateStatisticalUM2: model.statisticalMeasurementUnits?.shouldCalculateStatisticalUM2,
              ShouldCalculateStatisticalUM3: model.statisticalMeasurementUnits?.shouldCalculateStatistical,
            },
            AvailableUms: model.availableUms ?? ['Base'],
            VisibleUms: model.visibleUms,
          }) as OrderModel
      ),
    } as ConfigAgent)
  }

  private readonly getOutlet = async (id: string): Promise<void> => {
    const outlet = (await this.uveApiService.getPromise(Endpoints.OUTLET_DETAIL, {}, [
      {id: '{customerID}', value: id},
    ])) as any
    this.outlet.set(outlet)
  }

  private readonly buildAction = (task: TaskCloud, actionId = '-1'): void => {
    if (actionId === '-1') {
      task = {
        id: -1,
        actionId: -1,
        title: 'Pedido',
        description: 'Pedido',
        type: {
          id: 7,
          name: 'Pedido',
        },
        mandatory: true,
        image: null,
        group: null,
        enableComments: false,
        attachedDocumentId: null,
        executionType: 1,
        url: null,
        openInNewTab: false,
        orderModel: null,
      }
    }
    const action = {
      id: actionId,
      name: task.title,
      type: task.type.id,
      task: {
        id: task.id,
        name: task.title,
        associatedOrderModel: task.orderModel,
      },
      applicability: [{key: 'allcustomers'}],
    }
    this.actions.set([action])
  }

  private readonly getOrders = async (outletId: string, orderId: string): Promise<void> => {
    this.orders.set([
      ...this.orders(),
      ...(orderId ? await this.getOrderById(orderId, null) : await this.getOutletOrders(outletId)),
    ])
  }

  private readonly getOutletOrders = async (outletId: string): Promise<any[]> => {
    const year = new Date().getFullYear()
    const date = new Date().toISOString()
    const dateIni = date.replaceAll(`${year}-`, `${year - 1}-`)
    const ordersList = (await this.uveApiService.getPromise(
      Endpoints.ORDERS,
      {
        columns: 'id,orderId,statusFlag,customerName,orderDate',
        filters: `{"statusFlag":"BL,IN,CF,EN,AC,NS,SP","outletMainId":"${outletId}","orderDate":"${dateIni};${date}"}`,
        limit: 30,
        offset: 0,
        order: 'orderDate;desc',
      },
      []
    )) as any

    if (ordersList?.data?.length > 0) return await this.getOrdersDetails(ordersList.data.map(o => o.id))

    return []
  }

  private readonly getOrderById = async (orderId: string, order: any): Promise<any[]> => {
    order ??= (await this.uveApiService.getPromise(Endpoints.ORDER_DETAIL, null, [
      {id: '{orderId}', value: orderId},
    ])) as any

    const offerReadOnlyTypes = ['OP', 'N+M']
    const freeOrderLines =
      this.allOrderLines()
        ?.orderLines?.filter(o => o.additionalInfo.uveId === ORDER_TYPE_LINES.FREE)
        .map(o => o.id) ?? []

    if (!this.outlet()) {
      await this.getOutlet(order.outletMainId)
    }

    order.products = order.products.map(p => ({
      ...p,
      agreementNum: undefined,
      readOnly: offerReadOnlyTypes.includes(p.offerType) && freeOrderLines.includes(p.typeLine),
      discountPercentage: this.discountPercentageFor(p),
    }))

    this.order.set(order)

    return order ? [order] : []
  }

  private readonly getOrdersDetails = async (ordersId: string[], extraStatus = ''): Promise<any[]> => {
    const ordersDetails = (await this.uveApiService.getPromise(
      Endpoints.ORDERS_DETAILS_BY_ID,
      {
        columns:
          'id,orderId,distributor,distributorCode,distributorName,distributorPhone,orderDate,outletId,outletMainId,deliveryDate,desiredDeliveryDate,customerName,customerId,customerCodeByUVE,customerFinalCodeByUVE,deliveryAddress,deliveryCity,status,statusFlag,deliveryAlert,totalOrderPrice,observations,orderType,orderModel',
        filters: JSON.stringify({statusFlag: 'BL,IN,CF,EN,AC,NS,SP' + extraStatus, ids: ordersId?.join(',')}),
        limit: 100,
        offset: 0,
        order: 'orderDate;desc',
      },
      []
    )) as any
    return (
      ordersDetails?.data?.map(o => ({
        ...o,
        products: o?.products?.map(p => ({
          ...p,
          agreementNum: undefined,
          discountPercentage: this.discountPercentageFor(p),
        })),
      })) ?? []
    )
  }

  private readonly getDistributors = async (order: any): Promise<void> => {
    const distributors = (await this.uveApiService.getPromise(Endpoints.DISTRIBUTORS_ORDER, null)) as any[]
    this.distributors.set(
      distributors.map(d => ({
        id: d.id,
        codeByManufacturer: d.additionalInfo?.visibleCode ?? d.additionalInfo?.CodigoDist,
        alternativeCodeByManufacturer: null,
        name: d.name,
        trade_name: d.name,
        business_name: d.name,
        vat: null,
        status: 'AC',
        logoUrl: d.additionalInfo?.imageUrl,
        hasCode: false,
        attributes: {
          street: [this.embeddedIdComercial],
          settlements: [],
          invoices: [],
          deliveryNotes: [],
          orders:
            d.additionalInfo.modulesEnabled.includes('Orders') || d.id === order?.distributorId ?
              [this.embeddedIdComercial]
            : [],
        },
      }))
    )
  }

  private readonly getRelationOutletData = async (outlet: Outlet, actions: Action[], order: any): Promise<void> => {
    const orderModelId = order?.model ?? actions[0]?.task?.associatedOrderModel
    const orderModel = this.configAgent()?.ordersConfigurationModels?.find(m => m.Id === orderModelId)

    if (!!orderModel && orderModel.Type === OrderType.PEDIDO_TRANSFER) {
      await this.getDistributors(order)
      const idcAgente = this.uveApiService.getSessionUser()?.organizationId?.toString()
      await Promise.all(
        this.distributors().map(async d => {
          const data = await this.uveApiService.getPromise(Endpoints.DISTRIBUTOR_CUSTOMER_INFO, null, [
            {id: '{distributorID}', value: d.id},
            {id: '{outletID}', value: outlet.id},
            {id: '{model}', value: orderModelId},
          ])
          return {distId: d.id, info: data}
        })
      ).then(distributorsInfo => {
        const outletRelationCodes = {[outlet.id]: {}}
        distributorsInfo.forEach(d => {
          if (!!d.info && d.info[0]) {
            outletRelationCodes[outlet.id][d.distId] = [
              {
                customerCodeByDistributor: d.info[0].additionalInfo.customerCode,
                idcClienteFinalDist: d.info[0].id,
              },
            ]
          } else {
            outletRelationCodes[outlet.id][idcAgente] = [
              {
                customerCodeByDistributor: outlet.code,
                idcClienteFinalDist: outlet.id,
              },
            ]
          }
        })

        this.outletRelationCodes.set(outletRelationCodes)
      })
    }
  }

  private readonly getImagesProducts = async (codes: string[]): Promise<void> => {
    const filters = {
      filters: JSON.stringify({
        code: codes.join(','),
        status: 'AC,BD,BJ,BT',
      }),
      fields: 'id,image,code,name',
      limit: codes.length,
      offset: 0,
    }

    await this.uveApiService.getPromiseNewApi(Endpoints.PRODUCTS_LIST_V3, filters, []).then((res: any) => {
      res?.data?.map(p => {
        const idcProducto = Number(p.id)
        if (!this.productsImageByIdcProduct.has(idcProducto) && !!p.image?.url) {
          this.productsImageByIdcProduct.set(idcProducto, {url: p.image.url})
        }
      })
    })
  }

  private readonly setPermissions = async (permissions: UserPermission[]): Promise<void> => {
    const mapper = {
      Audit: 'Cloud_Audit',
      Routes: 'Cloud_Rutas',
      Orders: 'Cloud_Pedidos',
      Assortments: 'Cloud_Surtidos',
      Customers: 'Cloud_Clientes',
      Reports: 'Cloud_Reports',
    }

    this.userPermissions.set(
      permissions.map(p => ({
        ...p,
        id: mapper[p.id] ?? p.id,
      })) ?? []
    )
  }

  private readonly setFeatureFlags = (payload: any): void => {
    this.featureFlags.set(FeatureFlags.StreetAiVoiceToOrder, !!payload['street_AiVoiceToOrder'])
  }

  private readonly deleteDraftOrder = (id: string, actionId: string): void => {
    this.apiService.deleteItemLocalInfo('DRAFT_ORDER_' + id + '_' + actionId)
  }

  private readonly downloadMultimediaOffers = async (): Promise<void> => {
    this.storageApiService.set(PreferencesStorageIds.AGREEMENTS, [])
  }

  private readonly setDataTaxes = (params: any[]): void => {
    const {
      TaxesList: taxes,
      TaxProfiles: taxesProfiles,
      Reapro_GastosEnvio: additionalCosts,
    } = params.reduce(
      (acc, {parameter, value}) => {
        acc[parameter] = value
        return acc
      },
      {} as Record<string, string>
    )

    this.dataTaxes.set({
      taxes: JsonUtil.safeParse<TaxEntity[]>(taxes),
      taxesProfiles: JsonUtil.safeParse<TaxProfile[]>(taxesProfiles),
      additionalCosts: JsonUtil.safeParse<AdditionalCost[]>(additionalCosts),
    })
  }
}
